/**
 * 二维码生成（QR Code Model 2，字节模式，纠错等级 L/M/Q/H）
 *
 * 项目里没引第三方二维码库，这里手写一版，避免为了一个弹窗新增依赖。
 * 只做 QR 的核心通路：字节模式（UTF-8）+ 自动选版本 + 掩码评分选优，
 * 不做的部分：汉字模式（中文按 UTF-8 字节走，体积略大但通用）、结构化追加、ECI。
 *
 * 用法：
 *   import { qrMatrix } from '@/utils/qrcode'
 *   const { size, modules } = qrMatrix('https://example.com?a=1', 'M')
 *   // modules 是 size 行 × size 列 的二维布尔数组，true 表示深色模块
 */

// 纠错等级 → 格式信息里的 2bit 编码（标准定义：L=01, M=00, Q=11, H=10）
const EC_LEVEL = { L: 1, M: 0, Q: 3, H: 2 }

// 纠错等级 → RS_BLOCK_TABLE 里的列索引（表格按 L/M/Q/H 顺序排）
const EC_ORDER = { L: 0, M: 1, Q: 2, H: 3 }

// 各版本 × 纠错等级的 RS 分块表
// 结构：RS_BLOCK_TABLE[版本-1][EC_ORDER[等级]] = [[块数, 每块数据码字数, 每块纠错码字数], ...]
const RS_BLOCK_TABLE = [
  // 版本 1
  [
    [[1, 19, 7]], [[1, 16, 10]], [[1, 13, 13]], [[1, 9, 17]]
  ],
  // 版本 2
  [
    [[1, 34, 10]], [[1, 28, 16]], [[1, 22, 22]], [[1, 16, 28]]
  ],
  // 版本 3
  [
    [[1, 55, 15]], [[1, 44, 26]], [[2, 17, 18]], [[2, 13, 22]]
  ],
  // 版本 4
  [
    [[1, 80, 20]], [[2, 32, 18]], [[2, 24, 26]], [[4, 9, 16]]
  ],
  // 版本 5
  [
    [[1, 108, 26]], [[2, 43, 24]], [[2, 15, 18], [2, 16, 18]], [[2, 11, 22], [2, 12, 22]]
  ],
  // 版本 6
  [
    [[2, 68, 18]], [[4, 27, 16]], [[4, 19, 24]], [[4, 15, 28]]
  ],
  // 版本 7
  [
    [[2, 78, 20]], [[4, 31, 18]], [[2, 14, 18], [4, 15, 18]], [[4, 13, 26], [1, 14, 26]]
  ],
  // 版本 8
  [
    [[2, 97, 24]], [[2, 38, 22], [2, 39, 22]], [[4, 18, 22], [2, 19, 22]], [[4, 14, 26], [2, 15, 26]]
  ],
  // 版本 9
  [
    [[2, 116, 30]], [[3, 36, 22], [2, 37, 22]], [[4, 16, 20], [4, 17, 20]], [[4, 12, 24], [4, 13, 24]]
  ],
  // 版本 10
  [
    [[2, 68, 18], [2, 69, 18]], [[4, 43, 26], [1, 44, 26]], [[6, 19, 24], [2, 20, 24]], [[6, 15, 28], [2, 16, 28]]
  ]
]

// 各版本对齐图案中心坐标
const ALIGNMENT_PATTERN = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54]
]

// 版本 1–10 的格式信息（BCH 编码后异或掩码），索引 [2bit纠错编码][mask]
const FORMAT_INFO = (() => {
  const table = []
  // 2bit 值 0..3 依次对应 M(00)、L(01)、H(10)、Q(11)
  const ecBitsByIndex = [0, 1, 2, 3]
  for (let ec = 0; ec < 4; ec++) {
    const row = []
    for (let mask = 0; mask < 8; mask++) {
      const data = (ecBitsByIndex[ec] << 3) | mask
      let rem = data
      for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
      row.push(((data << 10) | rem) ^ 0x5412)
    }
    table.push(row)
  }
  return table
})()

// 版本信息（≥7 才有），BCH(18,6)
const VERSION_INFO = (() => {
  const table = []
  for (let v = 7; v <= 10; v++) {
    let rem = v
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25)
    table[v] = (v << 12) | rem
  }
  return table
})()

/** UTF-8 编码成字节数组 */
function utf8Bytes(str) {
  const out = []
  for (let i = 0; i < str.length; i++) {
    let code = str.charCodeAt(i)
    if (code < 0x80) {
      out.push(code)
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f))
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // 代理对：取出低位算出完整码点
      const next = str.charCodeAt(++i)
      code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00)
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f)
      )
    } else {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f))
    }
  }
  return out
}

/** 某版本某纠错等级能装的数据码字数（字节模式下还要扣掉头部） */
function dataCapacityBytes(version, ecIdx) {
  const blocks = RS_BLOCK_TABLE[version - 1][ecIdx]
  let total = 0
  for (const [count, dataLen] of blocks) total += count * dataLen
  // 字节模式头：4bit 模式 + 8/16bit 长度，向下取整到字节
  const headerBits = 4 + (version < 10 ? 8 : 16)
  return Math.floor((total * 8 - headerBits) / 8)
}

/** 选能装下的最小版本 */
function pickVersion(byteLen, ecIdx) {
  for (let v = 1; v <= RS_BLOCK_TABLE.length; v++) {
    if (dataCapacityBytes(v, ecIdx) >= byteLen) return v
  }
  throw new Error('内容过长，超过版本 10 的容量上限')
}

/** 生成数据码字（含模式、长度、填充） */
function buildDataCodewords(bytes, version, ecIdx) {
  const bits = []
  const pushBits = (val, len) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1)
  }
  pushBits(0b0100, 4) // 字节模式
  pushBits(bytes.length, version < 10 ? 8 : 16)
  for (const b of bytes) pushBits(b, 8)

  const blocks = RS_BLOCK_TABLE[version - 1][ecIdx]
  let totalData = 0
  for (const [count, dataLen] of blocks) totalData += count * dataLen
  const capacityBits = totalData * 8

  // 结束符最多 4bit
  for (let i = 0; i < 4 && bits.length < capacityBits; i++) bits.push(0)
  // 补齐到字节边界
  while (bits.length % 8 !== 0) bits.push(0)
  // 交替填充 0xEC / 0x11
  const padBytes = [0xec, 0x11]
  let padIdx = 0
  while (bits.length < capacityBits) {
    pushBits(padBytes[padIdx++ % 2], 8)
  }

  const codewords = []
  for (let i = 0; i < bits.length; i += 8) {
    let v = 0
    for (let j = 0; j < 8; j++) v = (v << 1) | bits[i + j]
    codewords.push(v)
  }
  return codewords
}

/** GF(256) 以 2 为底的幂表（模 0x11D），预先算好，供 gfMul 与生成多项式使用 */
const GF_EXP = (() => {
  const t = new Array(512)
  let x = 1
  for (let i = 0; i < 255; i++) {
    t[i] = x
    x = (x << 1) ^ ((x >>> 7) * 0x11d)
    x &= 0xff
  }
  for (let i = 255; i < 512; i++) t[i] = t[i - 255]
  return t
})()

/** 对数表，配合 GF_EXP 做乘法 */
const GF_LOG = (() => {
  const t = new Array(256).fill(0)
  for (let i = 0; i < 255; i++) t[GF_EXP[i]] = i
  return t
})()

function gfExp(n) {
  return GF_EXP[n % 255]
}

/** GF(256) 乘法：a*b = 2^(log a + log b) */
function gfMul(a, b) {
  if (a === 0 || b === 0) return 0
  return GF_EXP[GF_LOG[a] + GF_LOG[b]]
}

/** 生成 RS 纠错码字 */
function rsEncode(data, ecLen) {
  // 生成多项式系数（最高次项为 1，这里从 1 次项开始存）
  let gen = [1]
  for (let i = 0; i < ecLen; i++) {
    const next = new Array(gen.length + 1).fill(0)
    for (let j = 0; j < gen.length; j++) {
      next[j] ^= gen[j]
      next[j + 1] ^= gfMul(gen[j], gfExp(i))
    }
    gen = next
  }
  const res = new Array(ecLen).fill(0)
  for (const byte of data) {
    const factor = byte ^ res[0]
    res.shift()
    res.push(0)
    if (factor !== 0) {
      for (let i = 0; i < ecLen; i++) res[i] ^= gfMul(gen[i + 1], factor)
    }
  }
  return res
}

/** 交织数据块与纠错块，得到最终码字序列 */
function interleave(codewords, version, ecIdx) {
  const blocks = RS_BLOCK_TABLE[version - 1][ecIdx]
  const dataBlocks = []
  const ecBlocks = []
  let offset = 0
  for (const [count, dataLen, ecLen] of blocks) {
    for (let i = 0; i < count; i++) {
      const chunk = codewords.slice(offset, offset + dataLen)
      offset += dataLen
      dataBlocks.push(chunk)
      ecBlocks.push(rsEncode(chunk, ecLen))
    }
  }
  const maxData = Math.max(...dataBlocks.map(b => b.length))
  const maxEc = Math.max(...ecBlocks.map(b => b.length))
  const out = []
  for (let i = 0; i < maxData; i++) {
    for (const b of dataBlocks) if (i < b.length) out.push(b[i])
  }
  for (let i = 0; i < maxEc; i++) {
    for (const b of ecBlocks) if (i < b.length) out.push(b[i])
  }
  return out
}

/**
 * 生成二维码矩阵
 * @param {string} text 内容
 * @param {'L'|'M'|'Q'|'H'} ecLevel 纠错等级，默认 M
 * @returns {{ size: number, modules: boolean[][] }}
 */
export function qrMatrix(text, ecLevel = 'M') {
  // 分块表用 EC_ORDER 的列索引，格式信息用 EC_LEVEL 的 2bit 值，两者不能混
  const ecIdx = EC_ORDER[ecLevel]
  const ecBits = EC_LEVEL[ecLevel]
  const bytes = utf8Bytes(String(text))
  const version = pickVersion(bytes.length, ecIdx)
  const size = version * 4 + 17

  // ---- 1. 功能图案占位 ----
  const modules = Array.from({ length: size }, () => new Array(size).fill(false))
  const reserved = Array.from({ length: size }, () => new Array(size).fill(false))

  const setFunc = (x, y, dark) => {
    if (x < 0 || y < 0 || x >= size || y >= size) return
    modules[y][x] = dark
    reserved[y][x] = true
  }

  // 三个定位图案 + 分隔符
  const drawFinder = (cx, cy) => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx
        const y = cy + dy
        if (x < 0 || y < 0 || x >= size || y >= size) continue
        const dist = Math.max(Math.abs(dx), Math.abs(dy))
        setFunc(x, y, dist !== 2 && dist !== 4)
      }
    }
  }
  drawFinder(3, 3)
  drawFinder(size - 4, 3)
  drawFinder(3, size - 4)

  // 对齐图案
  const align = ALIGNMENT_PATTERN[version - 1]
  const last = align[align.length - 1]
  for (const cy of align) {
    for (const cx of align) {
      // 与定位图案重叠的位置跳过
      if ((cx === 6 && cy === 6) || (cx === 6 && cy === last) || (cx === last && cy === 6)) continue
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const dist = Math.max(Math.abs(dx), Math.abs(dy))
          setFunc(cx + dx, cy + dy, dist !== 1)
        }
      }
    }
  }

  // 定时图案
  for (let i = 8; i < size - 8; i++) {
    setFunc(6, i, i % 2 === 0)
    setFunc(i, 6, i % 2 === 0)
  }

  // 预留格式信息区（先占位，后面统一写）
  for (let i = 0; i < 9; i++) {
    if (!reserved[8][i]) setFunc(i, 8, false)
    if (!reserved[i][8]) setFunc(8, i, false)
  }
  for (let i = 0; i < 8; i++) {
    setFunc(size - 1 - i, 8, false)
    setFunc(8, size - 1 - i, false)
  }
  setFunc(8, size - 8, true) // 固定的深色模块

  // 版本信息（≥7）
  if (version >= 7) {
    const vi = VERSION_INFO[version]
    for (let i = 0; i < 18; i++) {
      const bit = ((vi >>> i) & 1) === 1
      const a = size - 11 + (i % 3)
      const b = Math.floor(i / 3)
      setFunc(a, b, bit)
      setFunc(b, a, bit)
    }
  }

  // ---- 2. 写入数据 ----
  const data = buildDataCodewords(bytes, version, ecIdx)
  const finalCodewords = interleave(data, version, ecIdx)
  const dataBits = []
  for (const cw of finalCodewords) {
    for (let i = 7; i >= 0; i--) dataBits.push((cw >>> i) & 1)
  }

  let bitIdx = 0
  let upward = true
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5 // 跳过垂直定时列
    for (let vert = 0; vert < size; vert++) {
      const y = upward ? size - 1 - vert : vert
      for (let j = 0; j < 2; j++) {
        const x = right - j
        if (reserved[y][x]) continue
        modules[y][x] = bitIdx < dataBits.length ? dataBits[bitIdx] === 1 : false
        bitIdx++
      }
    }
    upward = !upward
  }

  // ---- 3. 掩码评分，选最优 ----
  const applyMask = (maskId) => {
    const masked = modules.map(row => row.slice())
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (reserved[y][x]) continue
        let invert = false
        switch (maskId) {
          case 0: invert = (x + y) % 2 === 0; break
          case 1: invert = y % 2 === 0; break
          case 2: invert = x % 3 === 0; break
          case 3: invert = (x + y) % 3 === 0; break
          case 4: invert = (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0; break
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break
          case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break
        }
        if (invert) masked[y][x] = !masked[y][x]
      }
    }
    // 写格式信息（用 2bit 纠错编码索引，不是分块表索引）
    const fmt = FORMAT_INFO[ecBits][maskId]
    for (let i = 0; i < 15; i++) {
      const bit = ((fmt >>> i) & 1) === 1
      // 左上角竖排 + 横排
      if (i < 6) masked[i][8] = bit
      else if (i < 8) masked[i + 1][8] = bit
      else if (i < 9) masked[8][7] = bit
      else masked[8][14 - i] = bit
      // 右上 + 左下
      if (i < 8) masked[8][size - 1 - i] = bit
      else masked[size - 15 + i][8] = bit
    }
    return masked
  }

  const penalty = (m) => {
    let score = 0
    // 规则1：同色连续 ≥5
    for (let y = 0; y < size; y++) {
      let run = 1
      for (let x = 1; x < size; x++) {
        if (m[y][x] === m[y][x - 1]) run++
        else { if (run >= 5) score += run - 2; run = 1 }
      }
      if (run >= 5) score += run - 2
    }
    for (let x = 0; x < size; x++) {
      let run = 1
      for (let y = 1; y < size; y++) {
        if (m[y][x] === m[y - 1][x]) run++
        else { if (run >= 5) score += run - 2; run = 1 }
      }
      if (run >= 5) score += run - 2
    }
    // 规则2：2×2 同色块
    for (let y = 0; y < size - 1; y++) {
      for (let x = 0; x < size - 1; x++) {
        const v = m[y][x]
        if (v === m[y][x + 1] && v === m[y + 1][x] && v === m[y + 1][x + 1]) score += 3
      }
    }
    // 规则3：类似定位图案的 1:1:3:1:1（简化判定）
    const pattern = [true, false, true, true, true, false, true]
    const check = (get, len) => {
      let c = 0
      for (let i = 0; i + 7 <= len; i++) {
        let ok = true
        for (let k = 0; k < 7; k++) if (get(i + k) !== pattern[k]) { ok = false; break }
        if (ok) c++
      }
      return c * 40
    }
    for (let y = 0; y < size; y++) score += check(i => m[y][i], size)
    for (let x = 0; x < size; x++) score += check(i => m[i][x], size)
    // 规则4：深色比例偏离 50%
    let dark = 0
    for (const row of m) for (const v of row) if (v) dark++
    const ratio = (dark * 100) / (size * size)
    score += Math.floor(Math.abs(ratio - 50) / 5) * 10
    return score
  }

  let best = null
  let bestScore = Infinity
  for (let mask = 0; mask < 8; mask++) {
    const m = applyMask(mask)
    const s = penalty(m)
    if (s < bestScore) {
      bestScore = s
      best = m
    }
  }

  return { size, modules: best, version }
}

/**
 * 把矩阵画到 canvas 上
 * @param {HTMLCanvasElement} canvas
 * @param {string} text
 * @param {object} opts { size: 像素边长, margin: 边距模块数, dark, light, ecLevel, logoText }
 */
export function drawQrToCanvas(canvas, text, opts = {}) {
  const {
    size: px = 260,
    margin = 2,
    dark = '#1a1a1a',
    light = '#ffffff',
    ecLevel = 'M'
  } = opts

  const { size, modules } = qrMatrix(text, ecLevel)
  const dpr = window.devicePixelRatio || 1
  canvas.width = px * dpr
  canvas.height = px * dpr
  canvas.style.width = px + 'px'
  canvas.style.height = px + 'px'

  const ctx = canvas.getContext('2d')
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.scale(dpr, dpr)
  ctx.fillStyle = light
  ctx.fillRect(0, 0, px, px)

  const total = size + margin * 2
  const cell = px / total
  ctx.fillStyle = dark
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (!modules[y][x]) continue
      ctx.fillRect(
        Math.floor((x + margin) * cell),
        Math.floor((y + margin) * cell),
        Math.ceil(cell),
        Math.ceil(cell)
      )
    }
  }
  return { version: size }
}
